import { ShieldTick } from 'iconsax-react';
import { isNotNil } from 'rambda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouteLoaderData } from 'react-router';
import { useTypedParams, useTypedSearchParams, useTypedState } from 'react-router-typesafe-routes';
import HeaderActionButton from '@/components/common/header-action-button';
import ConversationVirtuaList from '@/components/conversations/conversation-list/conversation-virtua-list';
import ConversationList from '@/components/conversations/conversation-list/conversation-virtuoso-list';
import { ConversationNoticeBar } from '@/components/conversations/conversation-notice-bar';
import ConversationProviders from '@/components/conversations/conversation-providers';
import MessageStateIndicator from '@/components/conversations/message-state-indicator';
import SessionSettingMenu from '@/components/conversations/session-setting-menu';
import { WebSocketStateIndicator } from '@/components/conversations/web-socket-state-indicator';
import AppHeader from '@/components/layouts/page-layout/app-header';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useGroupInfo from '@/hooks/groups/use-group-info';
import { useGroupMessages } from '@/hooks/groups/use-group-messages';
import useResetScrollYOnFocus from '@/hooks/use-reset-scroll-y-on-focus';
import useRotateResize from '@/hooks/use-rotate-resize';
import useUserPlatform from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import type { GroupMessagesLayoutLoaderReturnType } from '@/routes/messages/groups/_layout.loader';
import { useConfigurationStore } from '@/stores/configuration';
import { createConversationStore } from '@/stores/conversation';
import { useLayoutStore } from '@/stores/layout-status';
import cn from '@/utils/merge-class-names';

export function GroupMessages({ groupId }: { groupId: number }) {
  const layoutLoaderData = useRouteLoaderData<GroupMessagesLayoutLoaderReturnType>(ROUTES.MESSAGES.GROUPS.$path());
  const { t } = useTranslation();
  const [{ search: paramsSearchId }] = useTypedSearchParams(ROUTES.MESSAGES.GROUPS);
  const { isDesktop, isEmbedded } = useUserPlatform();
  const { searchId: stateSearchId } = useLayoutStore();
  const searchId = isDesktop ? stateSearchId : paramsSearchId;
  const { from: navigatedFrom } = useTypedState(ROUTES.MESSAGES.GROUPS);
  const store = useMemo(() => createConversationStore('group', groupId), [groupId]);
  const { session, group, hasUnreadNotice, isHistoryLoading } = useGroupMessages(groupId, searchId);
  const { data: groupInfo, isChannel, canManage } = useGroupInfo(groupId);

  const { dispatch } = useCommDispatcher();
  const { enableNewChatList } = useConfigurationStore();

  const promise = useMemo(() => layoutLoaderData?.promises || false, [groupId]);

  useResetScrollYOnFocus();
  useRotateResize();

  const handleNavigateBack = () => {
    if (isEmbedded) {
      dispatch({
        event: 'exit-session',
        data: { type: 'group', id: groupId },
      });

      if (navigatedFrom === 'comm') dispatch({ event: 'dismiss' });
    }
  };

  return (
    <PageLayout
      title={session?.GroupName ?? group?.GroupName ?? t('conversation.group.title')}
      className='gap-0 pb-safe-or-0 has-[.ql-editor:focus]:mode-standalone:pb-0'>
      <AppHeader
        navigateBack={isDesktop ? false : Boolean(searchId || navigatedFrom === 'search') || true}
        onNavigateBack={handleNavigateBack}
        className='desktop:px-6 desktop:border-b border-base-200 mb-0'
        actions={
          <>
            <MessageStateIndicator />
            <WebSocketStateIndicator />
            <SessionSettingMenu
              type='group'
              id={groupId}
            />
          </>
        }>
        <Link
          className='w-full desktop:w-fit'
          to={
            isDesktop
              ? ROUTES.MESSAGES.GROUPS.SETTINGS.MEMBERS.$buildPath({
                  params: { id: groupId },
                })
              : ROUTES.MESSAGES.GROUPS.$.SETTINGS.$path({ relative: true })
          }
          state={
            navigatedFrom
              ? ROUTES.MESSAGES.GROUPS.SETTINGS.$buildState({
                  state: { from: 'comm' },
                })
              : undefined
          }>
          <div className='flex w-full items-center gap-2'>
            <h3 className='flex items-center gap-1 text-base font-bold'>
              <span className='line-clamp-1 max-w-fit flex-1 break-all'>{session?.GroupName ?? group?.GroupName}</span>

              {session?.GroupType === 1 ? (
                <ShieldTick
                  variant='Bold'
                  className='size-6 text-primary'
                />
              ) : (
                <span>{`(${group?.UserCount || 0})`}</span>
              )}

              {canManage &&
                !isChannel &&
                isNotNil(groupInfo?.GroupJoinApplyCount) &&
                groupInfo.GroupJoinApplyCount > 0 && (
                  <span
                    className={cn(
                      'ml-1 badge badge-accent bg-red-500 text-neutral-0 border-0 badge-xs size-4 text-[10px]',
                      groupInfo.GroupJoinApplyCount > 99 && 'w-6',
                    )}>
                    {groupInfo.GroupJoinApplyCount > 99 ? '99+' : groupInfo.GroupJoinApplyCount}
                  </span>
                )}
            </h3>
            <HeaderActionButton />
          </div>
        </Link>
      </AppHeader>

      {group && hasUnreadNotice && <ConversationNoticeBar group={group} />}

      <ConversationProviders
        type='group'
        id={groupId}
        store={store}>
        <LoadingLayout
          className='bg-base-100'
          promise={promise}>
          {() =>
            enableNewChatList ? (
              <ConversationVirtuaList
                isLoading={isHistoryLoading}
                searchId={searchId}
                initialUnreadCount={layoutLoaderData?.initialUnreadCount}
              />
            ) : (
              <ConversationList
                showHeader
                isLoading={isHistoryLoading}
                searchId={searchId}
                initialUnreadCount={layoutLoaderData?.initialUnreadCount}
                excludeTypes={canManage ? new Set() : undefined}
              />
            )
          }
        </LoadingLayout>
      </ConversationProviders>
    </PageLayout>
  );
}

export default function GroupMessagesByIdRoute() {
  const { id } = useTypedParams(ROUTES.MESSAGES.GROUPS);
  return <GroupMessages groupId={id} />;
}
